import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { quotesApi } from '@/services/quotes';
import { trackFileUpload, trackQuoteError } from '@/services/analytics';

interface UploadState {
  readonly isUploading: boolean;
  readonly progress: number;
  readonly error: string | null;
  readonly quoteId: string | null;
}

const INITIAL_STATE: UploadState = {
  isUploading: false,
  progress: 0,
  error: null,
  quoteId: null,
};

const sanitizeFileName = (name: string): string =>
  name.replace(/\s+/g, '_').replace(/[^A-Za-z0-9._\-]/g, '');

export const useFileUpload = () => {
  const [state, setState] = useState<UploadState>(INITIAL_STATE);
  const queryClient = useQueryClient();

  const upload = useCallback(async (file: File) => {
    setState({ isUploading: true, progress: 0, error: null, quoteId: null });

    const fileExt = file.name.split('.').pop()?.toLowerCase() ?? 'unknown';

    try {
      // Get pre-signed URL
      const { quoteId, uploadUrl } = await quotesApi.requestUploadUrl({
        fileName: sanitizeFileName(file.name),
        fileSize: file.size,
        contentType: 'application/octet-stream',
      });

      setState((prev) => ({ ...prev, progress: 30 }));

      // Upload directly to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error('Upload to storage failed');
      }

      setState({ isUploading: false, progress: 100, error: null, quoteId });

      trackFileUpload({
        file_type: fileExt,
        file_size_mb: Math.round((file.size / (1024 * 1024)) * 100) / 100,
      });

      // Invalidate quotes list so "My Quotes" page shows the new entry
      // without requiring a manual refresh.
      queryClient.invalidateQueries({ queryKey: ['quotes'] });

      return quoteId;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      setState({ isUploading: false, progress: 0, error: message, quoteId: null });
      trackQuoteError({ error_type: 'upload_failed' });
      return null;
    }
  }, [queryClient]);

  const reset = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  return { ...state, upload, reset };
};
